import { useTranslation } from "react-i18next";
import InitContext from "../pages/InitContext";
import React, { useContext, useEffect, useState } from "react";
import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import Disease from "../disease/Disease";
import MedicalData from "./MedicalData";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlus, faTrash} from "@fortawesome/free-solid-svg-icons";
import {Consts} from "../pages/consts";

const Patient = () => {
  const { t } = useTranslation();
  const [patientDiseases, setPatientDiseases] = useState([]);
  const [patientsDiseasesInDb, setPatientsDiseasesInDb] = useState([]);
  const context = useContext(InitContext);
  const [openAddDisease, setOpenAddDisease] = useState(false);
  const [patient, setPatient] = useState({});
  const [alreadyAddedError, setAlreadyAddedError] = useState(false);
  const { loading, error, clearError, getDbUser, getDbDiseases, deleteDbDisease, saveDbDisease } = useMmcService();

  useEffect(() => {
    clearError();
    getDbUser(context?.token, context?.patient?.id).then((patient) => setPatient(patient));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    clearError();

    if (patient?.id) {
      getDbDiseases(context?.token, patient?.id).then((data) =>
        setPatientsDiseasesInDb(data?.diseases));
    }
  }, [patient]);

  const handleDeleteDisease = (disease) => {
    if(disease?.id){ // the disease to delete was stored in the DB
      deleteDbDisease(context?.token, disease?.id).then((result) => {
        const newDiseases = diseasesToShow?.filter(d  => d?.id !== result?.id);
        setPatientsDiseasesInDb(newDiseases);
        setPatientDiseases([]);
      });
    } else { // the disease to delete is a new one, which is not yet stored in the DB
      const newDiseases = patientDiseases?.filter(d  => d?.icd10 !== disease?.icd10);
      setPatientDiseases(newDiseases);
    }
  };

  const saveDiseases = () => {
    clearError();

    patientDiseases?.forEach((disease) => {
      if (context?.token && context?.token !== "") {
        saveDbDisease(context?.token, disease, context?.patient?.id).then(
          (id) => {
            disease = {...disease, id};
            setPatientsDiseasesInDb([...patientsDiseasesInDb, disease])
            setPatientDiseases([]);
          }
        );
      }
    });
  }

  let message =
    error && error?.includes(Consts.PATIENT)
      ? error && error?.includes(Consts.GET)
        ? t("error_by_retrieving_patients_data")
        : t("error_by_updating_patients_data")
      : error && error?.includes(Consts.MEASUREMENT)
        ? error && error?.includes(Consts.GET)
          ? t("error_by_retrieving_patients_measurements")
          : t("error_by_saving_a_measurement")
        : t("error_by_retrieving_a_disease");

  const errorMessage = error && <ErrorMessage message={message} />;
  const spinner = loading && <Spinner />;
  let diseasesToShow = [];

  if(patientsDiseasesInDb?.length > 0){
    diseasesToShow = [...patientsDiseasesInDb];
  }

  if(patientDiseases?.length > 0){
    diseasesToShow = [...diseasesToShow, ...patientDiseases];
  }

  const localErrorMessage = alreadyAddedError && (
    <ErrorMessage message="The disease is already selected" />
  );

  return (
    <div className="w3-display-container content-container content-container-full">
      <div className="medication-view">
        <div style={{ width: '100%', textAlign: 'right', paddingRight: "20px" }}>
          <label>{t("username")}: {context?.patient?.login}, {t("role")}: {t("patient").toLowerCase()}</label>
        </div>
        <div className="w3-center form-display-drugs medication-content">
          <div className="w3-panel w3-card-4 record-panel">
            <header className="w3-container w3-center w3-padding-small">
              <h2>{t("your_medical_data")}</h2>
            </header>
            {errorMessage}
            {localErrorMessage}
            {spinner}
            <div className="w3-container patient-record-layout">
              <div className="w3-column w3-padding-small patient-record-layout__content">
                <div className="w3-container patient-record-layout__section patient-record-layout__section--medical">
                  <MedicalData patient={patient} />
                </div>
                <section className="patient-diseases-panel patient-record-layout__section patient-record-layout__section--diseases">
                  <div className="patient-diseases-panel__header">
                    <div>
                      <h3 className="patient-diseases-panel__title">{t("your_diseases")}</h3>
                      <p className="patient-diseases-panel__subtitle">
                        {diseasesToShow?.length || 0} {t("selected", { defaultValue: "selected" })}
                      </p>
                    </div>
                    <div className="patient-diseases-panel__actions">
                      <button
                        type="button"
                        className="w3-btn w3-round-xlarge w3-theme-d5"
                        onClick={() => setOpenAddDisease(true)}
                      >
                        <FontAwesomeIcon icon={faPlus} /> {t("add_new_disease")}
                      </button>
                      <button
                        className={`w3-btn w3-round-xlarge ${patientDiseases?.length > 0 ? "w3-red" : "w3-grey w3-disabled"}`}
                        onClick={saveDiseases}
                        type="button"
                      >
                        {t("save_diseases")}
                      </button>
                    </div>
                  </div>
                  <div className="patient-diseases-list">
                    {diseasesToShow?.length > 0 ? (
                      diseasesToShow?.map((disease) => (
                        <article className="patient-disease-card" key={disease?.id || disease?.icd10}>
                          <div className="patient-disease-card__content">
                            <div className="patient-disease-card__text">
                              <span className="patient-disease-card__name">{disease?.icd10name}</span>
                              <span className="patient-disease-card__code">(ICD-10: {disease?.icd10})</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteDisease(disease)}
                            className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--danger"
                            aria-label={t("delete", { defaultValue: "Delete" })}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </article>
                      ))
                    ) : (
                      <div className="patient-diseases-empty">{t("not_available")}</div>
                    )}
                  </div>
                </section>
                {openAddDisease && (
                  <Disease
                    showDialog={openAddDisease}
                    closeDialog={() => setOpenAddDisease(false)}
                    patientDiseases={patientDiseases}
                    onSetPatientDiseases={setPatientDiseases}
                    diseasesToShow={diseasesToShow}
                    setAlreadyAddedError={value => setAlreadyAddedError(value)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Patient;
